import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { CORAL_700, GRAY_900 } from 'constants/styling/theme'
import { DeleteOutlineOutlined, Event } from '@mui/icons-material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FilterType, usePropertyDashboardFilter } from '../_main/context/PropertyDashboardFilter.context'

import Box from '@mui/material/Box'
import { DateRangePicker } from 'components/common/DateRangePicker'
import { FC } from 'react'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { Params } from 'constants/misc'
import Stack from '@mui/material/Stack'
import { useTimezone } from 'components/contexts/timezone.context'
import { useTranslation } from 'react-i18next'

/**
 * @component Properties date filter which contains date range picker for filtering properties by creation date.
 * 
 * @example
 * <PropertiesDateFilter />
 */
export const PropertiesDateFilter: FC = () => {

  const { t } = useTranslation(['properties_dashboard'])
  const { userTimezone } = useTimezone()

  const {
    propertyParams,
    openFilter,
    setOpenFilter,
    handleCloseFilter,
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,
    tempDateRangeStart,
    setTempDateRangeStart,
    tempDateRangeEnd,
    setTempDateRangeEnd,
  } = usePropertyDashboardFilter()

  /** Handles clearing both temp and actual date range values */
  const handleClearDateRange = () => {
    setTempDateRangeStart(null)
    setTempDateRangeEnd(null)
    setDateRangeStart(null)
    setDateRangeEnd(null)
    propertyParams.set(
      [Params.PROPERTY_CREATION_DATE_START, ''],
      [Params.PROPERTY_CREATION_DATE_END, '']
    )
  }

  /** Handles temp date range changes (for display only) */
  const handleTempDateRangeChange = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    setTempDateRangeStart(startDate)
    setTempDateRangeEnd(endDate)
  }

  /** Handles actual date range changes (applies the filter) */
  const handleDateRangeChange = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    setDateRangeStart(startDate)
    setDateRangeEnd(endDate)

    if (startDate && endDate) {
      propertyParams.set(
        [Params.PROPERTY_CREATION_DATE_START, startDate.format('YYYY-MM-DD')],
        [Params.PROPERTY_CREATION_DATE_END, endDate.format('YYYY-MM-DD')]
      )
      logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.DATE_RANGE })
    } else {
      propertyParams.set(
        [Params.PROPERTY_CREATION_DATE_START, ''],
        [Params.PROPERTY_CREATION_DATE_END, '']
      )
    }
  }

  return (
    <Box sx={{ width: 'calc(24rem + 2.3rem)' }}>
      <MUIDropdown
        open={openFilter === FilterType.DATE_RANGE}
        onClose={handleCloseFilter}
        onBlur={() => { }} // Prevent closing on blur
        button={(isOpen, action, onClose) => (
          <MUIDropdownInput
            readOnly={true}
            value={
              openFilter === FilterType.DATE_RANGE
                ? (tempDateRangeStart && tempDateRangeEnd
                  ? `${tempDateRangeStart.format('DD/MM/YYYY')} to ${tempDateRangeEnd.format('DD/MM/YYYY')}`
                  : tempDateRangeStart
                    ? `${tempDateRangeStart.format('DD/MM/YYYY')} to End date`
                    : '')
                : (dateRangeStart && dateRangeEnd
                  ? `${dateRangeStart.format('DD/MM/YYYY')} to ${dateRangeEnd.format('DD/MM/YYYY')}`
                  : '')
            }
            placeholder={t('start_date_to_end_date')}
            type={DropdownInputTypes.SELECTOR}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '1.6rem',
                fontWeight: 400
              }
            }}
            onClick={(e) => {
              logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.DATE_RANGE })
              setOpenFilter(FilterType.DATE_RANGE)
              // Initialize temp state with current values
              setTempDateRangeStart(dateRangeStart)
              setTempDateRangeEnd(dateRangeEnd)
              action(e)
            }}
            customInputContent={!!(openFilter === FilterType.DATE_RANGE ? tempDateRangeStart && tempDateRangeEnd : dateRangeStart && dateRangeEnd) ? (
              <Stack
                flexDirection="row"
                fontSize="1.4rem"
                paddingRight="0.4rem"
                alignItems="center"
                paddingTop="0.1rem"
                color={GRAY_900}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  // Clear both temp and actual values, plus URL params
                  handleClearDateRange()
                }}
                sx={{ cursor: 'pointer', ':hover': { color: CORAL_700 } }}
              >
                <DeleteOutlineOutlined fontSize="inherit" />
              </Stack>
            ) : (
              <Stack
                flexDirection="row"
                paddingLeft="0"
                paddingRight="0.4rem"
                fontSize="1.4rem"
                alignItems="center"
                paddingTop="0.1rem"
                color={GRAY_900}
              >
                <Event fontSize="inherit" />
              </Stack>
            )}
          />
        )}
      >
        <Box width="33.8rem">
          <DateRangePicker
            startDate={tempDateRangeStart}
            endDate={tempDateRangeEnd}
            onDateRangeChange={handleDateRangeChange}
            onTempDateRangeChange={handleTempDateRangeChange}
            onClear={handleClearDateRange}
            onClose={handleCloseFilter}
            timezone={userTimezone}
            allowPastOnly={true}
          />
        </Box>
      </MUIDropdown>
    </Box>
  )
}
